/**
 * Embudo de una campaña del oficial: enviados → entregados → leídos → respondieron, y aparte
 * bajas y fallidos. Copia adaptada de `campana-nueva/components/DeliveryFunnel.tsx`: aquel habla
 * de email y de Baileys, y no tiene bajas ni fallidos.
 *
 * Mismas decisiones de forma: una serie, un color (la longitud ya codifica la magnitud) y el
 * denominador siempre visible («de X enviados · Y %»): un porcentaje suelto no dice sobre qué base
 * está. «Volvieron a comprar» sólo aparece medido: un 0 ahí se leería «nadie volvió» cuando la
 * verdad es «todavía no lo medimos». Es sobre los que recibieron y sin grupo de comparación (no hay
 * control en WhatsApp oficial): la barra no es el efecto del mensaje y así lo dice. Con un público de la
 * Base o el Público de Activa se mide con la base de ventas: «Compraron», sobre los que están en ella.
 */

import { Send, CheckCheck, MessageSquare, TrendingUp, UserMinus, XCircle } from "lucide-react";
import type { Embudo, Medicion } from "../types";
import { num, pct, plural } from "../../campana-nueva/utils/format";

interface Props {
  embudo: Embudo | null;
  simulado: boolean;
  medicion?: Medicion | null;
  leidoAclaracion?: string;
  /** Si el pedido de los números falló: se dice, nunca se pintan ceros. */
  error?: string;
}

const pctTexto = (fraccion: number) => `${Math.round((Number(fraccion) || 0) * 1000) / 10}`.replace(".", ",");

export function EmbudoOficial({ embudo, simulado, medicion, leidoAclaracion, error }: Props) {
  const env = embudo?.enviados || 0;
  // Un backend anterior al 27/10/2026 no medía la Base ni el Público: «Sin medir» y la barra punteada sugerirían una
  // medición pendiente que con él nunca llega. Su propio código decía de dónde vino la audiencia.
  const sinMedicionBase = !!medicion && !medicion.disponible && (medicion.motivo === "AUDIENCIA_BASE" || medicion.motivo === "AUDIENCIA_PUBLICO");
  // Con la base de ventas, la tasa es sobre los que están en ella (de los demás no se sabe): se dice sobre cuántos.
  const ventas = medicion?.disponible && medicion.atribucion === "base-de-ventas" ? medicion : null;
  const grupoVentas = ventas ? (ventas.entregados.n === 0 ? ventas.envio : ventas.entregados) : null;
  const medido = grupoVentas ?? (medicion?.disponible ? medicion.entregados : null);
  const origenSinMedicion = medicion && !medicion.disponible && medicion.motivo === "AUDIENCIA_PUBLICO" ? "Público de Activa" : "público de la base de datos";
  const filas = embudo
    ? [
      { label: "Enviados", val: env, icon: Send, nota: "salieron hacia WhatsApp" },
      { label: "Entregados", val: embudo.entregados, icon: CheckCheck, nota: "✓✓ llegaron al teléfono" },
      { label: "Leídos", val: embudo.leidos, icon: CheckCheck, nota: "✓✓ azul" },
      { label: "Respondieron", val: embudo.respondieron, icon: MessageSquare, nota: "contestaron o tocaron un botón" },
    ]
    : [];

  return (
    <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm" aria-label="Estado de entrega">
      <header className="flex items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-base font-semibold text-slate-900 tracking-tight">Estado de entrega</h2>
          <p className="text-xs text-slate-500 mt-0.5">Qué pasó con cada mensaje después de salir</p>
        </div>
        {simulado ? (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-lg bg-amber-100 text-amber-700 flex-shrink-0">Simulado</span>
        ) : (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 flex-shrink-0">Real</span>
        )}
      </header>

      {!embudo ? (
        <p className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-500">
          {error || "No pudimos traer los resultados de esta campaña."}
        </p>
      ) : (
        <>
          <div className="space-y-4">
            {filas.map((f) => {
              const p = pct(f.val, env);
              const Icon = f.icon;
              return (
                <div key={f.label}>
                  <div className="flex items-baseline justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon size={14} className="text-slate-400 flex-shrink-0" />
                      <span className="text-[13px] font-medium text-slate-700">{f.label}</span>
                      <span className="text-[11px] text-slate-500 truncate hidden sm:inline">· {f.nota}</span>
                    </div>
                    <div className="flex items-baseline gap-2 flex-shrink-0">
                      <span className="text-[15px] font-semibold text-slate-900">{num(f.val)}</span>
                      {f.label !== "Enviados" && <span className="text-[11px] text-slate-400">de {num(env)} {plural(env, "enviado", "enviados")} · {p} %</span>}
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-violet-100/70 overflow-hidden">
                    <div className="h-full rounded-full bg-violet-600 transition-all duration-700 ease-out" style={{ width: `${Math.max(1, p)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bajas y fallidos no son un paso del embudo: son lo que se perdió en el camino. Las bajas van sobre los
              ENVIADOS, como cada renglón de arriba y como la tasa de Resultado: una baja puede no haberse entregado (el
              131050, quien ya había frenado las promociones desde WhatsApp), y sobre los entregados el porcentaje
              mezclaba bases. */}
          <div className="mt-5 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center justify-between rounded-lg bg-red-50 px-3 py-2">
              <span className="flex items-center gap-2 text-[13px] font-medium text-red-700"><UserMinus size={14} /> Bajas</span>
              <span className="text-[13px] text-red-700"><strong>{num(embudo.bajas)}</strong> <span className="text-[11px]">de {num(env)} {plural(env, "enviado", "enviados")} · {pct(embudo.bajas, env)} %</span></span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
              <span className="flex items-center gap-2 text-[13px] font-medium text-slate-700"><XCircle size={14} /> Fallidos</span>
              <span className="text-[13px] text-slate-700"><strong>{num(embudo.fallidos)}</strong></span>
            </div>
          </div>

          <div className="mt-5 pt-5 border-t border-slate-100">
            {sinMedicionBase ? (
              <p className="flex items-center gap-2 text-[13px] text-slate-400">
                <TrendingUp size={14} className="flex-shrink-0" /> Sin medición de recompra: {origenSinMedicion}
              </p>
            ) : (
            <>
            <div className="flex items-baseline justify-between gap-3 mb-1.5">
              <div className="flex items-center gap-2">
                <TrendingUp size={14} className="text-emerald-500 flex-shrink-0" />
                <span className="text-[13px] font-medium text-slate-700">{ventas ? "Compraron" : "Volvieron a comprar"}</span>
              </div>
              {medido ? (
                <div className="flex items-baseline gap-2">
                  <span className="text-[15px] font-semibold text-emerald-700">{num(medido.volvieron)}</span>
                  <span className="text-[11px] text-slate-400">
                    {grupoVentas ? `de ${num(grupoVentas.enLaBase)} en tu base de ventas` : `de ${num(medido.n)} que lo recibieron`} · {pctTexto(medido.tasa)} %
                  </span>
                </div>
              ) : (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-500">Sin medir</span>
              )}
            </div>
            <div className={`h-2 rounded-full overflow-hidden ${medido ? "bg-emerald-100" : "bg-slate-50 border border-dashed border-slate-200"}`}>
              {medido && (
                <div className="h-full rounded-full bg-emerald-600 transition-all duration-700 ease-out" style={{ width: `${Math.max(1, Math.min(100, (Number(medido.tasa) || 0) * 100))}%` }} />
              )}
            </div>
            {medicion?.disponible && <p className="text-[11px] text-slate-400 mt-1.5">Sin grupo de comparación: no es el efecto del mensaje.</p>}
            </>
            )}
          </div>

          {leidoAclaracion && <p className="text-[11px] text-slate-400 mt-5 leading-relaxed">{leidoAclaracion}</p>}
        </>
      )}
    </section>
  );
}

export default EmbudoOficial;
